const initModal = () => {
    const triggers = document.querySelectorAll('[data-modal-trigger]');
    const closeButtons = document.querySelectorAll('.modal__close');
    const autoModals = document.querySelectorAll('[data-modal-delay]');
    const STORAGE_KEY = 'global_modal_shown';
    const DURATION_DAYS = 15;
    const GLOBAL_MODAL_ID = 'modal--global';

    // Track whether the modal has been triggered in this session
    let hasTriggeredGlobalModal = false;

    // Helper function to check if we should show global modal
    const shouldShowGlobalModal = () => {
        if (hasTriggeredGlobalModal) return false;

        const stored = localStorage.getItem(STORAGE_KEY);
        if ( ! stored ) return true;

        const data = JSON.parse(stored);
        const now = new Date().getTime();
        
        // Check if the stored timestamp is older than DURATION_DAYS
        return now - data.timestamp > (DURATION_DAYS * 24 * 60 * 60 * 1000);
    };

    // Helper function to mark global modal as shown
    const markGlobalModalAsShown = () => {
        const data = {
            timestamp: new Date().getTime(),
            shown: true
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    };

    const close = () => {
        const activeModal = document.querySelector('.modal.is-active');
        if (activeModal) {
            // Remove any iframe inside the modal. Both code paths that
            // put an iframe in here (YouTube embeds, server-rendered
            // <template> clones) inject dynamically on open, so close
            // can treat them uniformly. Removing — rather than just
            // hiding — is what halts YouTube audio playback and what
            // resets stateful embeds (understand.com player navigates
            // internally; a fresh iframe means the next open starts
            // back at the original URL).
            const container = activeModal.querySelector('.modal__container');
            const iframe = container.querySelector('iframe');
            if (iframe) {
                iframe.remove();
            }

            activeModal.classList.remove('is-active');
            document.body.style.overflow = '';
            document.documentElement.style.overflow = '';
        }

        // If this is the global modal, mark it as shown
        if (activeModal.id === GLOBAL_MODAL_ID) {
            markGlobalModalAsShown();
        }
    };
 
    const open = (modalId) => {
        const modal = document.querySelector(`#${modalId}`);
        if (!modal) return;

        // Check if this is the global modal
        const isGlobalModal = modal.id === GLOBAL_MODAL_ID;
        
        // If it's a global modal and we shouldn't show it, return early
        if (isGlobalModal) {
            if (!shouldShowGlobalModal()) return;
            hasTriggeredGlobalModal = true;
        }

        const videoId = modal.dataset.videoId;
        const videoTitle = modal.dataset.videoTitle;

        // If there is a video, generate a YouTube embed.
        if (videoId) {
            const content = modal.querySelector('.modal__content');
            const iframe = createYouTubeEmbed(videoId, videoTitle);
            content.appendChild(iframe);
        }

        // If there's a server-rendered iframe <template>, clone it into
        // place. The template wrapper keeps the iframe inert at page
        // load (no network fetch, no autoplay); each open clones a
        // fresh copy so internal navigation/state doesn't survive
        // close → reopen.
        const iframeTemplate = modal.querySelector('template.modal__iframe-template');
        if (iframeTemplate) {
            const content = modal.querySelector('.modal__content');
            content.appendChild(iframeTemplate.content.cloneNode(true));

            // Reset the "click to play" hint so it re-appears for this
            // open even if the user dismissed it on a previous open.
            const hint = modal.querySelector('.modal__iframe-hint');
            if (hint) hint.classList.remove('modal__iframe-hint--dismissed');
        }

        modal.classList.add('is-active');
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
    };

    const handleTriggerClick = (e) => {
        e.preventDefault();
        const trigger = e.target.closest('[data-modal-trigger]');
        const modalId = trigger.dataset.modalTarget;
        open(modalId);
    };

    const handleOverlayClick = (e) => {
        if (e.target.classList.contains('modal__overlay')) {
            close();
        }
    };

    const createYouTubeEmbed = (videoId, title) => {
        const iframe = document.createElement('iframe');
        iframe.width = '100%';
        iframe.height = '100%';
        iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=1&playsinline=1`;
        iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
        iframe.allowFullscreen = true;
        iframe.title = (title ? title : 'YouTube video player');
        iframe.frameBorder = '0';
        iframe.referrerPolicy = 'strict-origin-when-cross-origin';
        
        return iframe;
    };

    // Initialize time-delayed modals
    const initDelayedModals = () => {
        if ( ! shouldShowGlobalModal() ) return;

        autoModals.forEach(modal => {
            const delay = parseInt(modal.dataset.modalDelay, 10);
            if (!isNaN(delay) && delay > 0) {
                setTimeout(() => {
                    open(modal.id);
                }, delay * 1000); // Convert to milliseconds
            }
        });
    };

    // Initialize exit-intent modals
    const initExitModals = () => {
        const globalModal = document.getElementById(GLOBAL_MODAL_ID);
        if ( ! globalModal || ! shouldShowGlobalModal() ) return;

        // Only proceed if the global modal has exit intent enabled
        if ( ! globalModal.hasAttribute('data-modal-exit' ) ) return;

        const handleExit = (e) => {
            // Only trigger if mouse leaves through top of window
            if ( e.clientY <= 0 ) {
                open(GLOBAL_MODAL_ID);

                // Remove the event listener after showing the modal
                document.removeEventListener('mouseleave', handleExit);
            }
        };

        // Only add exit detection on desktop
        if ( ! ('ontouchstart' in window) ) {
            document.addEventListener('mouseleave', handleExit);
        }
    };

    // Dismiss the "click to unmute" hint when the user interacts with the
    // iframe. We can't detect clicks INSIDE a cross-origin iframe directly
    // (Same-Origin Policy blocks event bubbling out), but when focus
    // transfers into an iframe, the parent window fires `blur` AND
    // document.activeElement becomes that iframe element. Combining the
    // two signals is reliable across browsers and avoids dismissing the
    // hint on alt-tab or other unrelated window blurs.
    const handleWindowBlur = () => {
        // Defer the check — activeElement updates after blur fires.
        setTimeout(() => {
            const focused = document.activeElement;
            if (!focused || focused.tagName !== 'IFRAME') return;
            const modal = focused.closest('.modal');
            const hint = modal && modal.querySelector('.modal__iframe-hint');
            if (hint) hint.classList.add('modal__iframe-hint--dismissed');
        }, 0);
    };

    // Wait for DOM to be ready
    document.addEventListener('DOMContentLoaded', () => {
        // Initialize click triggers
        triggers.forEach(trigger => {
            trigger.addEventListener('click', handleTriggerClick);
        });

        closeButtons.forEach(button => {
            button.addEventListener('click', close);
        });

        document.querySelectorAll('.modal').forEach(modal => {
            modal.addEventListener('click', handleOverlayClick);
        });

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') close();
        });

        // Listen for parent-window blur — triggered when the user clicks
        // into the iframe (because focus transfers into the iframe's
        // window). That's our cue to hide the unmute hint.
        window.addEventListener('blur', handleWindowBlur);

        // Initialize delayed and exit modals
        initDelayedModals();
        initExitModals();
    });
};

export { initModal };